import React, { useState, useEffect } from "react";
import { X, Save, RefreshCcw } from "lucide-react";
import { adminLifecycleService } from "../../../../services/api/settings";
import { LifecyclePhase } from "../../../../store/types/settings";

interface EditLifecyclePhaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: LifecyclePhase | null; // null means create mode
  onSuccess: () => void;
}

const EditLifecyclePhaseModal: React.FC<EditLifecyclePhaseModalProps> = ({
  isOpen,
  onClose,
  data,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isMandatory: false,
    setupFee: 0,
    monthlySubscription: 0,
    year1Total: 0,
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        description: data.description || "",
        isMandatory: !!data.isMandatory,
        setupFee: data.setupFee || 0,
        monthlySubscription: data.monthlySubscription || 0,
        year1Total: data.year1Total || 0,
      });
    } else {
      setFormData({
        name: "",
        description: "",
        isMandatory: false,
        setupFee: 0,
        monthlySubscription: 0,
        year1Total: 0,
      });
    }
  }, [data, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        setupFee: Number(formData.setupFee),
        monthlySubscription: Number(formData.monthlySubscription),
      };
      if (data) {
        await adminLifecycleService.phases.update(data.id, payload);
      } else {
        await adminLifecycleService.phases.create(payload);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Save phase failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {data ? "Edit Lifecycle Phase" : "Add Lifecycle Phase"}
            </h2>
            <p className="text-sm text-gray-500 font-medium">
              {data
                ? "Update phase configuration"
                : "Create a new phase module"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto flex-1 flex flex-col"
        >
          <div className="p-6 space-y-6 flex-1">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                  Phase Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g. Onboarding, Offboarding"
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                  Description
                </label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Enter phase details..."
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    Setup Fee (USD)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={formData.setupFee}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        setupFee: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    Monthly Subscription (USD)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={formData.monthlySubscription}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        monthlySubscription: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">
                    Year 1 Total (USD)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={formData.year1Total}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        year1Total: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none font-mono"
                  />
                </div>
              </div>

              <div className="flex items-end pt-2">
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl w-full border border-transparent hover:border-primary/10 transition-all">
                  <input
                    type="checkbox"
                    checked={formData.isMandatory}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isMandatory: e.target.checked,
                      })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary transition-all cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-bold text-gray-700 block">
                      Mandatory Phase
                    </span>
                    <span className="text-xs text-gray-400 font-medium">
                      If checked, users cannot toggle off this phase during
                      checkout.
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-gray-100 flex items-center justify-between bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-8 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all shadow-md shadow-primary/20 font-bold disabled:opacity-50"
            >
              {loading ? (
                <RefreshCcw size={20} className="animate-spin" />
              ) : (
                <Save size={20} />
              )}
              <span>{loading ? "Saving..." : "Save Phase"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLifecyclePhaseModal;
